import { storableError } from '../util/errors';
import { getAllUsers, getArtistsWithListings } from '../util/api';

// ================ Action types ================ //

export const FETCH_ARTIST_USERNAMES_REQUEST = 'app/artistUsernames/FETCH_ARTIST_USERNAMES_REQUEST';
export const FETCH_ARTIST_USERNAMES_SUCCESS = 'app/artistUsernames/FETCH_ARTIST_USERNAMES_SUCCESS';
export const FETCH_ARTIST_USERNAMES_ERROR = 'app/artistUsernames/FETCH_ARTIST_USERNAMES_ERROR';

export const FETCH_ARTISTS_WITH_LISTINGS_REQUEST = 'app/artistUsernames/FETCH_ARTISTS_WITH_LISTINGS_REQUEST';
export const FETCH_ARTISTS_WITH_LISTINGS_SUCCESS = 'app/artistUsernames/FETCH_ARTISTS_WITH_LISTINGS_SUCCESS';
export const FETCH_ARTISTS_WITH_LISTINGS_ERROR = 'app/artistUsernames/FETCH_ARTISTS_WITH_LISTINGS_ERROR';

export const FETCH_ART_ARTISTS_REQUEST = 'app/artistUsernames/FETCH_ART_ARTISTS_REQUEST';
export const FETCH_ART_ARTISTS_SUCCESS = 'app/artistUsernames/FETCH_ART_ARTISTS_SUCCESS';
export const FETCH_ART_ARTISTS_ERROR = 'app/artistUsernames/FETCH_ART_ARTISTS_ERROR';

export const FETCH_ART_CATEGORY_ARTISTS_REQUEST = 'app/artistUsernames/FETCH_ART_CATEGORY_ARTISTS_REQUEST';
export const FETCH_ART_CATEGORY_ARTISTS_SUCCESS = 'app/artistUsernames/FETCH_ART_CATEGORY_ARTISTS_SUCCESS';
export const FETCH_ART_CATEGORY_ARTISTS_ERROR = 'app/artistUsernames/FETCH_ART_CATEGORY_ARTISTS_ERROR';

export const FETCH_FEATURED_ART_CATEGORY_ARTISTS_REQUEST = 'app/artistUsernames/FETCH_FEATURED_ART_CATEGORY_ARTISTS_REQUEST';
export const FETCH_FEATURED_ART_CATEGORY_ARTISTS_SUCCESS = 'app/artistUsernames/FETCH_FEATURED_ART_CATEGORY_ARTISTS_SUCCESS';
export const FETCH_FEATURED_ART_CATEGORY_ARTISTS_ERROR = 'app/artistUsernames/FETCH_FEATURED_ART_CATEGORY_ARTISTS_ERROR';

export const FETCH_JUDAICA_CATEGORY_ARTISTS_REQUEST = 'app/artistUsernames/FETCH_JUDAICA_CATEGORY_ARTISTS_REQUEST';
export const FETCH_JUDAICA_CATEGORY_ARTISTS_SUCCESS = 'app/artistUsernames/FETCH_JUDAICA_CATEGORY_ARTISTS_SUCCESS';
export const FETCH_JUDAICA_CATEGORY_ARTISTS_ERROR = 'app/artistUsernames/FETCH_JUDAICA_CATEGORY_ARTISTS_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Raw data from the API (object with userId keys)
  usernamesData: {},
  // Processed array of artists for directory
  artists: [],
  // Username to ID mapping for profile lookups
  usernameToIdMapping: {},
  // Loading and error states
  fetchInProgress: false,
  fetchError: null,
  // Timestamp for cache validation
  lastFetched: null,
  // Artists with their listing images - simplified
  artistsWithListings: null,
  artistImages: null,
  fetchArtistsWithListingsInProgress: false,
  fetchArtistsWithListingsError: null,
  // Art category specific artists
  artArtists: [],
  fetchArtArtistsInProgress: false,
  fetchArtArtistsError: null,
  // Art category artists for landing page
  artCategoryArtists: [],
  fetchArtCategoryArtistsInProgress: false,
  fetchArtCategoryArtistsError: null,
  // Featured art category artists for landing page
  featuredArtCategoryArtists: [],
  fetchFeaturedArtCategoryArtistsInProgress: false,
  fetchFeaturedArtCategoryArtistsError: null,
  // Judaica category artists for landing page
  judaicaCategoryArtists: [],
  fetchJudaicaCategoryArtistsInProgress: false,
  fetchJudaicaCategoryArtistsError: null,
};

export default function artistUsernamesReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ARTIST_USERNAMES_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_ARTIST_USERNAMES_SUCCESS:
      const { data: usernamesData, timestamp } = payload;
      
      // Create username to ID mapping for profile page lookups
      const usernameToIdMapping = Object.entries(usernamesData).reduce((mapping, [userId, userData]) => {
        if (userData.username) {
          mapping[userData.username.toLowerCase()] = userId;
        }
        return mapping;
      }, {});
      
      // Create processed artists array for directory page
      const artists = Object.entries(usernamesData)
        .map(([userId, userData]) => userData)
        .filter(artist => artist.hasPostListingsPermission && artist.username)
        .sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });
      
      return {
        ...state,
        usernamesData,
        artists,
        usernameToIdMapping,
        fetchInProgress: false,
        lastFetched: timestamp,
      };
    case FETCH_ARTIST_USERNAMES_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };
    case FETCH_ARTISTS_WITH_LISTINGS_REQUEST:
      return {
        ...state,
        fetchArtistsWithListingsInProgress: true,
        fetchArtistsWithListingsError: null,
      };
    case FETCH_ARTISTS_WITH_LISTINGS_SUCCESS:
      const { artists: artistsData, artistImages } = payload;
      return {
        ...state,
        artistsWithListings: artistsData,
        artistImages: artistImages,
        fetchArtistsWithListingsInProgress: false,
      };
    case FETCH_ARTISTS_WITH_LISTINGS_ERROR:
      return {
        ...state,
        fetchArtistsWithListingsInProgress: false,
        fetchArtistsWithListingsError: payload,
      };
    case FETCH_ART_ARTISTS_REQUEST:
      return {
        ...state,
        fetchArtArtistsInProgress: true,
        fetchArtArtistsError: null,
      };
    case FETCH_ART_ARTISTS_SUCCESS:
      return {
        ...state,
        artArtists: payload,
        fetchArtArtistsInProgress: false,
      };
    case FETCH_ART_ARTISTS_ERROR:
      return {
        ...state,
        fetchArtArtistsError: payload,
        fetchArtArtistsInProgress: false,
      };
    case FETCH_ART_CATEGORY_ARTISTS_REQUEST:
      return {
        ...state,
        fetchArtCategoryArtistsInProgress: true,
        fetchArtCategoryArtistsError: null,
      };
    case FETCH_ART_CATEGORY_ARTISTS_SUCCESS:
      return {
        ...state,
        artCategoryArtists: payload,
        fetchArtCategoryArtistsInProgress: false,
      };
    case FETCH_ART_CATEGORY_ARTISTS_ERROR:
      return {
        ...state,
        fetchArtCategoryArtistsError: payload,
        fetchArtCategoryArtistsInProgress: false,
      };
    case FETCH_FEATURED_ART_CATEGORY_ARTISTS_REQUEST:
      return {
        ...state,
        fetchFeaturedArtCategoryArtistsInProgress: true,
        fetchFeaturedArtCategoryArtistsError: null,
      };
    case FETCH_FEATURED_ART_CATEGORY_ARTISTS_SUCCESS:
      return {
        ...state,
        featuredArtCategoryArtists: payload,
        fetchFeaturedArtCategoryArtistsInProgress: false,
      };
    case FETCH_FEATURED_ART_CATEGORY_ARTISTS_ERROR:
      return {
        ...state,
        fetchFeaturedArtCategoryArtistsError: payload,
        fetchFeaturedArtCategoryArtistsInProgress: false,
      };
    case FETCH_JUDAICA_CATEGORY_ARTISTS_REQUEST:
      return {
        ...state,
        fetchJudaicaCategoryArtistsInProgress: true,
        fetchJudaicaCategoryArtistsError: null,
      };
    case FETCH_JUDAICA_CATEGORY_ARTISTS_SUCCESS:
      return {
        ...state,
        judaicaCategoryArtists: payload,
        fetchJudaicaCategoryArtistsInProgress: false,
      };
    case FETCH_JUDAICA_CATEGORY_ARTISTS_ERROR:
      return {
        ...state,
        fetchJudaicaCategoryArtistsError: payload,
        fetchJudaicaCategoryArtistsInProgress: false,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchArtistUsernamesRequest = () => ({ type: FETCH_ARTIST_USERNAMES_REQUEST });
export const fetchArtistUsernamesSuccess = (data, timestamp) => ({
  type: FETCH_ARTIST_USERNAMES_SUCCESS,
  payload: { data, timestamp },
});
export const fetchArtistUsernamesError = (error) => ({
  type: FETCH_ARTIST_USERNAMES_ERROR,
  payload: error,
});

export const fetchArtistsWithListingsRequest = () => ({ type: FETCH_ARTISTS_WITH_LISTINGS_REQUEST });
export const fetchArtistsWithListingsSuccess = (artists, artistImages) => ({
  type: FETCH_ARTISTS_WITH_LISTINGS_SUCCESS,
  payload: { artists, artistImages },
});
export const fetchArtistsWithListingsError = (error) => ({
  type: FETCH_ARTISTS_WITH_LISTINGS_ERROR,
  payload: error,
});

export const fetchArtArtistsRequest = () => ({ type: FETCH_ART_ARTISTS_REQUEST });
export const fetchArtArtistsSuccess = (artists) => ({
  type: FETCH_ART_ARTISTS_SUCCESS,
  payload: artists,
});
export const fetchArtArtistsError = (error) => ({
  type: FETCH_ART_ARTISTS_ERROR,
  payload: error,
});

export const fetchArtCategoryArtistsRequest = () => ({ type: FETCH_ART_CATEGORY_ARTISTS_REQUEST });
export const fetchArtCategoryArtistsSuccess = (artists) => ({
  type: FETCH_ART_CATEGORY_ARTISTS_SUCCESS,
  payload: artists,
});
export const fetchArtCategoryArtistsError = (error) => ({
  type: FETCH_ART_CATEGORY_ARTISTS_ERROR,
  payload: error,
});

export const fetchFeaturedArtCategoryArtistsRequest = () => ({ type: FETCH_FEATURED_ART_CATEGORY_ARTISTS_REQUEST });
export const fetchFeaturedArtCategoryArtistsSuccess = (artists) => ({
  type: FETCH_FEATURED_ART_CATEGORY_ARTISTS_SUCCESS,
  payload: artists,
});
export const fetchFeaturedArtCategoryArtistsError = (error) => ({
  type: FETCH_FEATURED_ART_CATEGORY_ARTISTS_ERROR,
  payload: error,
});

export const fetchJudaicaCategoryArtistsRequest = () => ({ type: FETCH_JUDAICA_CATEGORY_ARTISTS_REQUEST });
export const fetchJudaicaCategoryArtistsSuccess = (artists) => ({
  type: FETCH_JUDAICA_CATEGORY_ARTISTS_SUCCESS,
  payload: artists,
});
export const fetchJudaicaCategoryArtistsError = (error) => ({
  type: FETCH_JUDAICA_CATEGORY_ARTISTS_ERROR,
  payload: error,
});

// ================ Selectors ================ //

export const getArtists = (state) => state.artistUsernames.artists;
export const getUsernameToIdMapping = (state) => state.artistUsernames.usernameToIdMapping;
export const getArtistUsernamesData = (state) => state.artistUsernames.usernamesData;
export const isArtistUsernamesFetchInProgress = (state) => state.artistUsernames.fetchInProgress;
export const getArtistUsernamesError = (state) => state.artistUsernames.fetchError;
export const getArtistUsernamesLastFetched = (state) => state.artistUsernames.lastFetched;

export const getArtistsWithListingsData = (state) => state.artistUsernames.artistsWithListings;
export const getArtistImages = (state) => state.artistUsernames.artistImages;
export const isArtistsWithListingsFetchInProgress = (state) => state.artistUsernames.fetchArtistsWithListingsInProgress;
export const getArtistsWithListingsError = (state) => state.artistUsernames.fetchArtistsWithListingsError;

export const getArtArtists = (state) => state.artistUsernames.artArtists;
export const isArtArtistsFetchInProgress = (state) => state.artistUsernames.fetchArtArtistsInProgress;
export const getArtArtistsError = (state) => state.artistUsernames.fetchArtArtistsError;

export const getArtCategoryArtists = (state) => state.artistUsernames.artCategoryArtists;
export const isArtCategoryArtistsFetchInProgress = (state) => state.artistUsernames.fetchArtCategoryArtistsInProgress;
export const getArtCategoryArtistsError = (state) => state.artistUsernames.fetchArtCategoryArtistsError;

export const getFeaturedArtCategoryArtists = (state) => state.artistUsernames.featuredArtCategoryArtists;
export const isFeaturedArtCategoryArtistsFetchInProgress = (state) => state.artistUsernames.fetchFeaturedArtCategoryArtistsInProgress;
export const getFeaturedArtCategoryArtistsError = (state) => state.artistUsernames.fetchFeaturedArtCategoryArtistsError;

export const getJudaicaCategoryArtists = (state) => state.artistUsernames.judaicaCategoryArtists;
export const isJudaicaCategoryArtistsFetchInProgress = (state) => state.artistUsernames.fetchJudaicaCategoryArtistsInProgress;
export const getJudaicaCategoryArtistsError = (state) => state.artistUsernames.fetchJudaicaCategoryArtistsError;

// Cache TTL: 15 minutes
const CACHE_TTL = 15 * 60 * 1000;

export const isArtistUsernamesCacheValid = (state) => {
  const lastFetched = getArtistUsernamesLastFetched(state);
  if (!lastFetched) return false;
  
  const now = Date.now();
  const cacheAge = now - lastFetched;
  return cacheAge < CACHE_TTL;
};

// ================ Thunks ================ //

export const fetchArtistUsernames = (forceRefresh = false) => (dispatch, getState) => {
  const state = getState();
  
  // Check if we need to fetch (not in progress, cache invalid, or force refresh)
  const inProgress = isArtistUsernamesFetchInProgress(state);
  const cacheValid = isArtistUsernamesCacheValid(state);
  
  if (inProgress) {
    return Promise.resolve();
  }
  
  if (!forceRefresh && cacheValid) {
    return Promise.resolve();
  }
  
  dispatch(fetchArtistUsernamesRequest());
  
  // Note: do NOT pass isDiscoverable here. This fetch builds the
  // username→userId mapping used to resolve /artist/:username profile URLs,
  // which must work for every artist (including non-discoverable ones).
  // The /artists directory uses fetchArtArtists, which applies its own
  // isDiscoverable filter.
  return getAllUsers({
    includeProfile: true,
    userType: 'artist',
  })
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Failed to fetch artist usernames');
      }
      
      const timestamp = Date.now();
      dispatch(fetchArtistUsernamesSuccess(response.data, timestamp));
      
      return response.data;
    })
    .catch(error => {
      dispatch(fetchArtistUsernamesError(storableError(error)));
      throw error;
    });
};

export const fetchArtistsWithListings = () => (dispatch, getState) => {
  const state = getState();
  
  // // Don't fetch if already in progress
  // const inProgress = isArtistsWithListingsFetchInProgress(state);
  // if (inProgress) {
  //   return Promise.resolve();
  // }
  
  // // Don't fetch if we already have data
  // const existingData = getArtistsWithListingsData(state);
  // if (existingData) {
  //   return Promise.resolve();
  // }
  
  dispatch(fetchArtistsWithListingsRequest());
  
  return getArtistsWithListings()
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Failed to fetch artists with listings');
      }
      
      const { artists, artistImages } = response.data;
      dispatch(fetchArtistsWithListingsSuccess(artists, artistImages));
      
      return response.data;
    })
    .catch(error => {
      dispatch(fetchArtistsWithListingsError(storableError(error)));
      throw error;
    });
};

export const fetchArtArtists = (forceRefresh = false) => (dispatch, getState) => {
  dispatch(fetchArtArtistsRequest());
  
  return getAllUsers({ 
    includeProfile: true, 
    userType: 'artist',
    sort: 'artistRating',
    isDiscoverable: true
  })
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Failed to fetch art artists');
      }
      
      // Process the data similar to fetchArtistUsernames but for art category
      const artArtists = Object.entries(response.data)
        .map(([userId, userData]) => userData)
      
      dispatch(fetchArtArtistsSuccess(artArtists));
      return artArtists;
    })
    .catch(error => {
      dispatch(fetchArtArtistsError(storableError(error)));
      throw error;
    });
};

export const fetchArtCategoryArtists = (forceRefresh = false) => (dispatch, getState) => {
  dispatch(fetchArtCategoryArtistsRequest());
  
  return getAllUsers({ 
    includeProfile: true, 
    userType: 'artist',
    sort: 'artistRating',
    artistCategory: 'art',
    isDiscoverable: true
  })
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Failed to fetch art category artists');
      }
      
      // Process the data similar to fetchArtistUsernames but filter for art category
      const artCategoryArtists = Object.entries(response.data)
        .map(([userId, userData]) => userData)
      
      dispatch(fetchArtCategoryArtistsSuccess(artCategoryArtists));
      return artCategoryArtists;
    })
    .catch(error => {
      dispatch(fetchArtCategoryArtistsError(storableError(error)));
      throw error;
    });
};

export const fetchFeaturedArtCategoryArtists = (forceRefresh = false) => (dispatch, getState) => {
  dispatch(fetchFeaturedArtCategoryArtistsRequest());
  
  return getAllUsers({ 
    includeProfile: true, 
    userType: 'artist',
    sort: 'artistRating',
    artistCategory: 'art',
    isFeatured: true
  })
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Failed to fetch featured art category artists');
      }
      
      // Process the data similar to fetchArtistUsernames but filter for featured art category
      const featuredArtCategoryArtists = Object.entries(response.data)
        .map(([userId, userData]) => userData)
      
      dispatch(fetchFeaturedArtCategoryArtistsSuccess(featuredArtCategoryArtists));
      return featuredArtCategoryArtists;
    })
    .catch(error => {
      dispatch(fetchFeaturedArtCategoryArtistsError(storableError(error)));
      throw error;
    });
};

export const fetchJudaicaCategoryArtists = (forceRefresh = false) => (dispatch, getState) => {
  dispatch(fetchJudaicaCategoryArtistsRequest());
  
  return getAllUsers({ 
    includeProfile: true, 
    userType: 'artist',
    sort: 'artistRating',
    artistCategory: 'judaica',
    isFeatured: true
  })
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Failed to fetch judaica category artists');
      }
      
      // Process the data similar to fetchArtistUsernames but filter for judaica category
      const judaicaCategoryArtists = Object.entries(response.data)
        .map(([userId, userData]) => userData)
      
      dispatch(fetchJudaicaCategoryArtistsSuccess(judaicaCategoryArtists));
      return judaicaCategoryArtists;
    })
    .catch(error => {
      dispatch(fetchJudaicaCategoryArtistsError(storableError(error)));
      throw error;
    });
};

// Helper function to get user ID by username (case-insensitive)
export const getUserIdByUsername = (state, username) => {
  const mapping = getUsernameToIdMapping(state);
  return mapping[username.toLowerCase()] || null;
}; 